import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import "react-image-crop/dist/ReactCrop.css";
import ReactCrop from "react-image-crop";
import IconButton from "../IconButton";
import Checkerboard from "../Checkerboard";
import { IconTrash } from "@rhumbix/rmbx_design_system_design_tokens";
const ImageUpload = ({ alt, canDelete, deleteLabel, maxHeight, maxWidth, onChange, onDelete, onLoaded, source, uploadLabel, }) => {
    const checkerboardRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageRef = useRef(null);
    const [isCropping, setIsCropping] = useState(false);
    const [aspect, setAspect] = useState(maxHeight / maxWidth);
    const [crop, setCrop] = useState();
    const [trigger, setTrigger] = useState(false);
    const [imageIsUpload, setImageIsUpload] = useState(false);
    const target = imageRef.current;
    // These are mutable because we need to adjust them
    // if the height exceeds our maximum  height
    let scale = 1;
    let width = maxWidth;
    let height = width * aspect;
    let offset = 0;
    // If fitting to width exceeds our maximum height
    // re-scale the image to fit our maximum height
    // and then center it
    if (height > maxHeight) {
        scale = maxHeight / height;
        width = width * scale;
        height = maxHeight;
        offset = Math.floor((maxWidth - width) / 2);
    }
    // Anytime the trigger is changed, that  means there's an update
    // to calculate and provide to the consumer
    useEffect(() => {
        if (imageRef.current && imageIsUpload) {
            const [cX, cY, cW, cH] = (crop && crop.width && crop.height)
                ? [crop.x, crop.y, crop.width, crop.height]
                : [0, 0, width, height];
            // The crop is in scaled coordinates, but the source image is in its original size
            // so we need to remove the scaling factor to get the source coordinates
            const f = imageRef.current.naturalWidth / width;
            const [sX, sY, sW, sH] = [cX * f, cY * f, cW * f, cH * f];
            const canvas = document.createElement('canvas');
            canvas.width = cW;
            canvas.height = cH;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                return;
            }
            ctx.drawImage(imageRef.current, sX, sY, sW, sH, 0, 0, cW, cH);
            canvas.toBlob(blob => { if (blob) {
                onChange(blob);
            } }, "image/png");
        }
    }, [trigger, imageIsUpload]);
    const handleOnCropChange = (crop) => { setCrop(crop); };
    const handleOnCropComplete = () => { setTrigger(!trigger); };
    const handleOnDelete = () => {
        if (!canDelete)
            return;
        // Reset everything to defaults
        imageRef.current = null;
        setAspect(maxHeight / maxWidth);
        setImageIsUpload(false);
        setCrop({ unit: "px", x: 0, y: 0, width: 0, height: 0 });
        setIsCropping(false);
        // And trigger the event
        onDelete();
    };
    const handleOnLoad = (target) => {
        if (target) {
            imageRef.current = target;
            if (target.naturalHeight && target.naturalWidth) {
                setAspect(target.naturalHeight / target.naturalWidth);
                setTrigger(!trigger);
            }
        }
        setCrop({ unit: "px", x: 0, y: 0, width: 0, height: 0 });
    };
    const handleOnSelectFile = (e) => {
        const target = e.target;
        if (target.files) {
            const url = URL.createObjectURL(target.files[0]);
            onLoaded(url);
            setIsCropping(true);
            setImageIsUpload(true);
        }
    };
    const handleOnUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    const style = { width: width, height: height };
    const image = React.createElement("img", { ref: imageRef, src: source, alt: alt, style: style, onLoad: (e) => { handleOnLoad(e.currentTarget); } });
    const croppableImage = isCropping
        ? (React.createElement(ReactCrop, { crop: crop, onChange: handleOnCropChange, onComplete: handleOnCropComplete }, image))
        : image;
    const checkerboard = React.createElement(Checkerboard, { ref: checkerboardRef },
        React.createElement("div", { style: { ...style, left: `${offset}px`, position: "relative" } }, croppableImage));
    const buttons = React.createElement(ButtonControls, null,
        React.createElement(IconButton, { text: uploadLabel, type: "button", onClick: handleOnUpload, theme: "rightRailPrimary" }),
        React.createElement(IconButton, { text: deleteLabel, type: "button", theme: "destructive", disabled: !canDelete, onClick: handleOnDelete, icon: color => React.createElement(IconTrash, { color: color }), style: { marginLeft: "auto" } }));
    return React.createElement("div", null,
        checkerboard,
        buttons,
        React.createElement("input", { id: "files", style: { display: "none" }, type: "file", ref: fileInputRef, accept: "image/*", onChange: handleOnSelectFile }));
};
const ButtonControls = styled.div `
    padding-top: 10px;
    height: 40px;
    display: flex;
    flex-direction: row;
`;
export default ImageUpload;
